import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Paper,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Warning as EmergencyIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import SectionContainer from '../components/SectionContainer';
import { services } from '../data/mockData';
import { useLanguage } from '../i18n/LanguageContext';

const Contact = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    contactMethod: 'email',
    email: '',
    phone: '',
    selectedServices: [],
    otherService: '',
    comments: '',
    images: [],
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (location.state?.selectedService) {
      const serviceId = location.state.selectedService;
      setFormData(prev => ({ ...prev, selectedServices: [serviceId] }));
    }
  }, [location.state]);

  const serviceOptions = services.map(service => ({
    id: service.id,
    label: t(`servicesData.${service.key}.title`),
  }));

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(serviceId)
        ? prev.selectedServices.filter(id => id !== serviceId)
        : [...prev.selectedServices, serviceId],
    }));
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const imageUrls = files.map(file => URL.createObjectURL(file));
    setFormData(prev => ({ ...prev, images: [...prev.images, ...imageUrls] }));
  };

  const handleImageRemove = (index) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (formData.contactMethod === 'email' && !formData.email) {
      newErrors.email = t('contact.errors.emailRequired');
    } else if (formData.contactMethod === 'phone' && !formData.phone) {
      newErrors.phone = t('contact.errors.phoneRequired');
    } else if (formData.contactMethod === 'both') {
      if (!formData.email) newErrors.email = t('contact.errors.emailRequired');
      if (!formData.phone) newErrors.phone = t('contact.errors.phoneRequired');
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('contact.errors.emailInvalid');
    }
    if (formData.phone && !/^\d{3}-\d{3}-\d{4}$/.test(formData.phone.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3'))) {
      newErrors.phone = t('contact.errors.phoneInvalid');
    }
    if (formData.selectedServices.length === 0 && !formData.otherService) {
      newErrors.services = t('contact.errors.servicesRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validateForm()) {
      setShowSuccess(true);
      setFormData({
        contactMethod: 'email',
        email: '',
        phone: '',
        selectedServices: [],
        otherService: '',
        comments: '',
        images: [],
      });
    }
  };

  const getServiceLabel = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? t(`servicesData.${service.key}.title`) : '';
  };

  return (
    <Box>
      <SectionContainer backgroundColor="grey.50" py={6}>
        <Typography variant="h1" textAlign="center" gutterBottom sx={{ mb: 2 }}>
          {t('contact.title')}
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          {t('contact.subtitle')}
        </Typography>
      </SectionContainer>

      <SectionContainer>
        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <Card elevation={3}>
              <CardContent sx={{ p: 4 }}>
                <form onSubmit={handleSubmit}>
                  <FormControl component="fieldset" sx={{ mb: 4 }}>
                    <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('contact.preferredMethod')}
                    </FormLabel>
                    <RadioGroup value={formData.contactMethod} onChange={handleInputChange('contactMethod')} row>
                      <FormControlLabel value="email" control={<Radio />} label={t('contact.email')} />
                      <FormControlLabel value="phone" control={<Radio />} label={t('contact.phone')} />
                      <FormControlLabel value="both" control={<Radio />} label={t('contact.both')} />
                    </RadioGroup>
                  </FormControl>

                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {(formData.contactMethod === 'email' || formData.contactMethod === 'both') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t('contact.emailLabel')}
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange('email')}
                          error={!!errors.email}
                          helperText={errors.email}
                          required={formData.contactMethod === 'email' || formData.contactMethod === 'both'}
                          InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                        />
                      </Grid>
                    )}
                    {(formData.contactMethod === 'phone' || formData.contactMethod === 'both') && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t('contact.phoneLabel')}
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange('phone')}
                          error={!!errors.phone}
                          helperText={errors.phone}
                          required={formData.contactMethod === 'phone' || formData.contactMethod === 'both'}
                          placeholder="555-555-5555"
                          InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {t('contact.servicesNeeded')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('contact.selectAll')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {serviceOptions.map((service) => (
                        <Chip
                          key={service.id}
                          label={service.label}
                          onClick={() => handleServiceToggle(service.id)}
                          color={formData.selectedServices.includes(service.id) ? 'primary' : 'default'}
                          variant={formData.selectedServices.includes(service.id) ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                      <Chip
                        label={t('contact.other')}
                        onClick={() => handleServiceToggle('other')}
                        color={formData.selectedServices.includes('other') ? 'primary' : 'default'}
                        variant={formData.selectedServices.includes('other') ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Box>

                    {formData.selectedServices.includes('other') && (
                      <TextField
                        fullWidth
                        label={t('contact.specifyOther')}
                        value={formData.otherService}
                        onChange={handleInputChange('otherService')}
                        sx={{ mb: 2 }}
                      />
                    )}

                    {errors.services && (
                      <Typography variant="body2" color="error">
                        {errors.services}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {t('contact.projectPhotos')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('contact.uploadHelp')}
                    </Typography>

                    <input accept="image/*" style={{ display: 'none' }} id="image-upload" multiple type="file" onChange={handleImageUpload} />
                    <label htmlFor="image-upload">
                      <Button variant="outlined" component="span" startIcon={<UploadIcon />} sx={{ mb: 2 }}>
                        {t('contact.uploadImages')}
                      </Button>
                    </label>

                    {formData.images.length > 0 && (
                      <Grid container spacing={2}>
                        {formData.images.map((image, index) => (
                          <Grid item xs={6} sm={4} md={3} key={index}>
                            <Box sx={{ position: 'relative' }}>
                              <Box
                                component="img"
                                src={image}
                                alt={`Upload ${index + 1}`}
                                sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 1 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleImageRemove(index)}
                                sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(0, 0, 0, 0.6)', color: 'white', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    label={t('contact.projectDetails')}
                    multiline
                    rows={4}
                    value={formData.comments}
                    onChange={handleInputChange('comments')}
                    placeholder={t('contact.projectDetailsPlaceholder')}
                    sx={{ mb: 4 }}
                  />

                  <Button type="submit" variant="contained" size="large" fullWidth sx={{ py: 2, fontSize: '1.1rem' }}>
                    {t('contact.sendRequest')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: 'error.main', color: 'white', textAlign: 'center' }}>
              <EmergencyIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('contact.urgentTitle')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                {t('contact.urgentText')}
              </Typography>
              <Button variant="contained" color="secondary" size="large" href="tel:555-555-5555" startIcon={<PhoneIcon />} fullWidth sx={{ fontWeight: 600 }}>
                555-555-5555
              </Button>
            </Paper>

            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('contact.whatToExpect')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>{t('contact.responseTimeLabel')}</strong> {t('contact.responseTimeText')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>{t('contact.quoteDeliveryLabel')}</strong> {t('contact.quoteDeliveryText')}
              </Typography>
              <Typography variant="body2">
                <strong>{t('contact.schedulingLabel')}</strong> {t('contact.schedulingText')}
              </Typography>
            </Paper>

            {formData.selectedServices.length > 0 && (
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('contact.selectedServices')}
                </Typography>
                {formData.selectedServices.map((serviceId) => (
                  <Chip
                    key={serviceId}
                    label={serviceId === 'other' ? t('contact.otherService') : getServiceLabel(serviceId)}
                    color="primary"
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Paper>
            )}
          </Grid>
        </Grid>
      </SectionContainer>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          {t('contact.successMessage')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
